import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CDataTable,
  CPagination,
  CRow,
} from "@coreui/react";
import { useCallback, useEffect, useState } from "react";
import { BsFillPlusCircleFill } from "react-icons/bs";
import { useHistory } from "react-router-dom";
import { getList } from "../../api/generics";
import { getRoutePath } from "../../routes";
import { SUCCESS } from "../../utils/constants/tags";
import { successAlert, warningAlert } from "../utils/messages";
import EstablishmentDeleteModal from "./EstablishmentDeleteModal";
import EstablishmentUpdateModal from "./EstablishmentUpdateModal";
import Establishment from "../../models/establishment";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { useIsMobile } from "../../hooks/is-mobile";
import { EstablishmentCard } from "./EstablishmentMobileCard";
import EstablishmentCrudActions from "./EstablishmentCrudActions";

const ITEMS_PER_PAGE = 10;

const EstablishmentCrud = () => {
  const fields = [
    { key: "code", _classes: ["text-center"], label: "Código" },
    { key: "denomination", _classes: ["text-center"], label: "Denominación" },
    { key: "address", _classes: ["text-center"], label: "Dirección" },
    {
      key: "departmentDescription",
      _classes: ["text-center"],
      label: "Departamento",
    },
    {
      key: "districtDescription",
      _classes: ["text-center"],
      label: "Distrito",
    },
    { key: "cityDescription", _classes: ["text-center"], label: "Ciudad" },
    { key: "phoneNumber", _classes: ["text-center"], label: "Teléfono" },
    { key: "email", _classes: ["text-center"], label: "Email" },
    {
      key: "actions",
      _classes: ["text-center"],
      label: "Acciones",
      filter: false,
    },
  ];

  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer,
  );

  const [showEstablishmentEditModal, setShowEstablishmentEditModal] =
    useState(false);
  const [showEstablishmentDeleteModal, setShowEstablishmentDeleteModal] =
    useState(false);
  const [establishment, setEstablishment] = useState<Establishment | undefined>(
    undefined,
  );

  const [loading, setLoading] = useState(true);
  const [establishments, setEstablishments] = useState<Establishment[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const history = useHistory();
  const getURLParams = () => {
    let urlParams = new URLSearchParams(history.location.search);
    let page = urlParams.get("page")
      ? parseInt(urlParams.get("page") as string)
      : 1;

    if (page <= 0) {
      page = 1;
    }

    return { page: page };
  };

  const fetchEstablishments = useCallback(async () => {
    const urlParams = getURLParams();
    const limit = ITEMS_PER_PAGE;
    const offset = ITEMS_PER_PAGE * Number(urlParams.page - 1);
    const additionalParams = new Map<string, string>();
    additionalParams.set("tax_payer_id", taxPayer.id!.toString());
    const itemsRequestStatus = await getList<Establishment>(
      "/establishments/",
      limit,
      offset,
      additionalParams,
    );

    if (itemsRequestStatus.status === SUCCESS) {
      if (itemsRequestStatus.data !== undefined) {
        const count = itemsRequestStatus.data.count;
        const pages = Math.ceil((count ? count : 0) / ITEMS_PER_PAGE);
        setTotalPages(pages);
        setEstablishments(itemsRequestStatus.data.items);
        setCurrentPage(urlParams.page);
      }
    } else {
      const message = itemsRequestStatus.detail
        ? itemsRequestStatus.detail
        : "Error desconocido";
      warningAlert(message);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxPayer]);

  const onPageChange = (page: number) => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    urlParams.set("page", page.toString());
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  /*const reset = () => {
    history.push(history.location.pathname);
  };*/

  const onAddClick = () => {
    setEstablishment(undefined);
    setShowEstablishmentEditModal(true);
  };

  const onUpdateClick = (establishment: Establishment) => {
    setEstablishment(establishment);
    setShowEstablishmentEditModal(true);
  };

  const onDeleteClick = (establishment: Establishment) => {
    setEstablishment(establishment);
    setShowEstablishmentDeleteModal(true);
  };

  const onEstablishmentEditCancel = () => {
    setShowEstablishmentEditModal(false);
  };

  const onEstablishmentEditSuccess = () => {
    successAlert("Datos guardados con éxito!");
    setShowEstablishmentEditModal(false);
    setEstablishments([]);
    setLoading(true);
    //setTotalPages(1);
    //setCurrentPage(1);
    //reset();
    fetchEstablishments();
  };

  const onEstablishmentDeleteCancel = () => {
    setShowEstablishmentDeleteModal(false);
  };

  const onEstablishmentDeleteSuccess = () => {
    successAlert("Registro eliminado con éxito!");
    setShowEstablishmentDeleteModal(false);
    setEstablishments([]);
    setLoading(true);
    fetchEstablishments();
  };

  useEffect(() => {
    fetchEstablishments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return history.listen((location) => {
      if (location.pathname !== getRoutePath("establishments")) {
        return;
      }
      fetchEstablishments();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  useEffect(() => {
    setLoading(true);
    fetchEstablishments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxPayer]);

  const isMobile = useIsMobile();

  return (
    <>
      <CRow>
        <CCol lg="12">
          <CCard>
            <CCardHeader className="d-flex flex-row mb-3">
              <div className="p-2 ">
                <h3>Establecimientos</h3>
              </div>
              {!taxPayer.isDemo && (
                <div className="p-2 ">
                  <CButton
                    color="primary"
                    className="float-right"
                    onClick={onAddClick}
                  >
                    <BsFillPlusCircleFill />
                    &nbsp; Nuevo
                  </CButton>
                </div>
              )}
            </CCardHeader>
            <CCardBody>
              <div>
                {isMobile ? (
                  // Mobile view: collapsible cards
                  <>
                    {establishments.length === 0 ? (
                      <h2 className="text-center">
                        No se encontraron registros
                      </h2>
                    ) : (
                      establishments.map((entry) => (
                        <EstablishmentCard
                          key={entry.id}
                          establishment={entry}
                          onUpdateClick={onUpdateClick}
                          onDeleteClick={onDeleteClick}
                          isDemo={!!taxPayer.isDemo}
                        />
                      ))
                    )}
                  </>
                ) : (
                  <CDataTable
                    noItemsView={{
                      noItems: "No se encontraron registros",
                      noResults: "No se encontraron registros",
                    }}
                    addTableClasses={"table-fixed"}
                    fields={fields}
                    items={establishments}
                    striped
                    border
                    loading={loading}
                    responsive
                    scopedSlots={{
                      address: (item: Establishment) => {
                        let strAddress = "";
                        if (item.address) {
                          strAddress = strAddress + " " + item.address;
                        }
                        if (item.houseNumber) {
                          strAddress = strAddress + ` Nª ${item.houseNumber}`;
                        }
                        if (item.complementaryAddress1) {
                          strAddress =
                            strAddress + `, ${item.complementaryAddress1}`;
                        }
                        if (item.complementaryAddress2) {
                          strAddress =
                            strAddress + `, ${item.complementaryAddress2}`;
                        }

                        return <td className="text-center">{strAddress}</td>;
                      },
                      phoneNumber: (item: Establishment) => {
                        return (
                          <td className="text-center">
                            {item.phoneNumber ? item.phoneNumber : "-"}
                          </td>
                        );
                      },
                      email: (item: Establishment) => {
                        return (
                          <td className="text-center">
                            {item.email ? item.email : "-"}
                          </td>
                        );
                      },
                      actions: (item: Establishment) => (
                        <EstablishmentCrudActions
                          establishment={item}
                          onDeleteClick={onDeleteClick}
                          onUpdateClick={onUpdateClick}
                          isDemo={!!taxPayer.isDemo}
                        />
                      ),
                    }}
                  />
                )}
                <div className="d-flex justify-content-center">
                  <CPagination
                    pages={totalPages}
                    activePage={currentPage}
                    onActivePageChange={(i: number) => onPageChange(i)}
                    className={totalPages < 2 ? "d-none" : ""}
                  />
                </div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <EstablishmentUpdateModal
        show={showEstablishmentEditModal}
        onCancel={onEstablishmentEditCancel}
        onSuccess={onEstablishmentEditSuccess}
        establishment={establishment}
      />
      <EstablishmentDeleteModal
        show={showEstablishmentDeleteModal}
        onCancel={onEstablishmentDeleteCancel}
        onSuccess={onEstablishmentDeleteSuccess}
        establishment={establishment}
      />
    </>
  );
};

export default EstablishmentCrud;
